import type {ScreenSprite} from '../types/spriteTypes'

const SPRITE_BASE_WIDTH = 400 // reference natural width at scale=1 in 1290-base
const FONT_BASE_WIDTH = 1290

/** Load a single image from URL, resolving with the decoded HTMLImageElement. */
const loadImage = (url: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const img = new Image()
    img.crossOrigin = 'anonymous'
    img.onload = () => resolve(img)
    img.onerror = () => reject(new Error(`Failed to load sprite image: ${url}`))
    img.src = url
  })

/** Preload all sprite images in parallel. Failures resolve to null so one bad URL doesn't kill the export. */
export const preloadSpriteImages = async (sprites: ScreenSprite[]): Promise<Map<string, HTMLImageElement>> => {
  const map = new Map<string, HTMLImageElement>()
  await Promise.all(
    sprites.map(async (s) => {
      if (!s.imageUrl) return
      try {
        const img = await loadImage(s.imageUrl)
        map.set(s.id, img)
      } catch {
        // Skip sprites that fail to load
      }
    }),
  )
  return map
}

/**
 * Map sprite positions from viewport-relative (0–1) to export-frame-relative (0–1).
 * Sprites whose center falls well outside the export frame are filtered out.
 */
export const mapSpritesToExportFrame = (
  sprites: ScreenSprite[],
  vpW: number,
  vpH: number,
  exW: number,
  exH: number,
): ScreenSprite[] => {
  const exAspect = exW / exH
  const vpAspect = vpW / vpH

  let frameW: number, frameH: number
  if (vpAspect > exAspect) {
    frameH = vpH
    frameW = vpH * exAspect
  } else {
    frameW = vpW
    frameH = vpW / exAspect
  }

  const offsetX = (vpW - frameW) / 2
  const offsetY = (vpH - frameH) / 2

  return sprites
    .map((s) => {
      const px = s.x * vpW
      const py = s.y * vpH
      const fx = (px - offsetX) / frameW
      const fy = (py - offsetY) / frameH
      return {...s, x: fx, y: fy}
    })
    .filter((s) => s.x >= -0.5 && s.x <= 1.5 && s.y >= -0.5 && s.y <= 1.5)
}

/**
 * Render sprites onto an offscreen canvas for export compositing. Positions are
 * normalized 0..1 mapped to the export dimensions.
 */
export const renderSpritesToCanvas = (
  sprites: ScreenSprite[],
  images: Map<string, HTMLImageElement>,
  width: number,
  height: number,
): HTMLCanvasElement => {
  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height
  const ctx = canvas.getContext('2d')!
  ctx.imageSmoothingEnabled = true
  ctx.imageSmoothingQuality = 'high'

  for (const s of sprites) {
    const img = images.get(s.id)
    if (!img) continue
    const aspect = s.imageAspect > 0 ? s.imageAspect : (img.naturalWidth || 1) / (img.naturalHeight || 1)
    // Long-side reference: SPRITE_BASE_WIDTH px in 1290-base, scaled to export width
    const longSide = (s.scale * SPRITE_BASE_WIDTH * width) / FONT_BASE_WIDTH
    const renderW = aspect >= 1 ? longSide : longSide * aspect
    const renderH = renderW / aspect

    const px = s.x * width
    const py = s.y * height

    ctx.save()
    ctx.translate(px, py)
    if (s.rotation !== 0) ctx.rotate((s.rotation * Math.PI) / 180)
    ctx.drawImage(img, -renderW / 2, -renderH / 2, renderW, renderH)
    ctx.restore()
  }

  return canvas
}
